import { Accordion, AccordionItem } from "shared/components/layout/Accordion"

import DevText from "components/DevText"
import { CheckboxForm } from "features/wizard/CheckboxForm.tsx"
import { RadioForm } from "features/wizard/RadioForm.tsx"

import PageWizard from "./PageWizard"
import * as schema from "./schema"
import {
    Actions,
    AnswerOptions,
    Section,
    useViewModel,
    ViewModel,
} from "./useViewModel"

// TODO: move shared constants/functions to separate file
// eslint-disable-next-line react-refresh/only-export-components
export enum WizardType {
    page = "page",
    accordion = "accordion",
}

type WizardProps = {
    wizardSchema: schema.Schema
    type?: WizardType
    color?: "primary" | "tertiary"
    onResults: (results: schema.Result[]) => void
    onComplete?: () => void
    onNextSection?: (section: string) => void
    onPreviousSection?: (section: string) => void
    onWizardStateChange?: (sectionIndex: number, questionIndex: number) => void
    showSectionResults?: boolean
    debugMode?: boolean
}
const Wizard = ({
    wizardSchema,
    onResults,
    onComplete,
    onNextSection,
    onPreviousSection,
    onWizardStateChange,
    type = WizardType.accordion,
    color = "primary",
    showSectionResults = false,
    debugMode = false,
}: WizardProps) => {
    const state: ViewModel = useViewModel(wizardSchema, { onResults })

    return (
        <>
            {type === WizardType.accordion && (
                <Accordion>
                    {state.sections.map(accordionBuilder(state.action))}
                </Accordion>
            )}
            {type === WizardType.page && (
                <PageWizard
                    state={state}
                    onComplete={onComplete}
                    onNextSection={onNextSection}
                    onPreviousSection={onPreviousSection}
                    onWizardStateChange={onWizardStateChange}
                    color={color}
                    showSectionResults={showSectionResults}
                />
            )}
            {debugMode && (
                <div>
                    <DevText>{JSON.stringify(state, null, 2)}</DevText>
                </div>
            )}
        </>
    )
}

export default Wizard

const accordionBuilder = (action: Actions) => (section: Section) => {
    return (
        <AccordionItem
            key={section.question.id}
            title={section.question.title}
            state={section.itemState}
            onToggle={(_, nextState) =>
                action.assignAccordionState({
                    id: section.question.id,
                    state: nextState,
                })
            }
        >
            <div>{section.question.info.description}</div>
            <FormSelect action={action} section={section} />
            {section.extraQuestions.map((extra) => (
                <>
                    <section>
                        <h2>{extra.question.title}</h2>{" "}
                        <div>{extra.question.info.description}</div>
                        <FormSelect action={action} section={extra} />
                    </section>
                </>
            ))}
        </AccordionItem>
    )
}

type FormSelectSection = {
    question: schema.CommonQuestion
    answers: AnswerOptions
    [_key: string]: any
}

function FormSelect({
    action,
    section,
}: {
    action: Actions
    section: FormSelectSection
}) {
    switch (section.question.type) {
        case schema.questionType.oneAnswer:
            return (
                <RadioForm
                    answers={section.answers}
                    onSubmit={(answer: string): void => {
                        action.assignSingleAnswer({
                            id: section.question.id,
                            value: answer,
                        })
                    }}
                />
            )
        case schema.questionType.anyThatApply:
            return (
                <CheckboxForm
                    answers={section.answers}
                    onSubmit={(selected: string[]): void => {
                        action.assignMultiAnswer({
                            id: section.question.id,
                            value: selected.map((id) => id),
                        })
                    }}
                />
            )
        default:
            throw new Error("missing case")
    }
}
